<template>
  <v-dialog
    v-model="open"
    persistent
    scrollable
    max-width="650px"
  >
    <v-card>
      <v-toolbar
        color="primary"
        dark
        class="pl-6"
      >
        <span class="text-h6 text-center">
          Editar Setor
        </span>
      </v-toolbar>

      <v-card-text
        class="h-auto"
      >
        <v-form ref="form" :disabled="loader.loading">
          <h3>Informações do Setor</h3>
          <v-row class="mt-3">
            <v-col
              cols="12"
              class="pb-0"
            >
              <v-text-field
                v-model="sector.name"
                label="Nome"
                variant="outlined"
                color="primary"
                density="compact"
                required
                :rules="[required]"
              />
            </v-col>
          </v-row>
          <v-row class="mt-4">
            <v-col
              cols="6"
              md="4"
              class="pb-0"
            >
              <v-select
                v-model="sector.active"
                :items="statusItems"
                label="Ativo"
                color="primary"
                variant="outlined"
                density="compact"
              />
            </v-col>
            <v-col
              cols="6"
              md="4"
              class="pb-0"
            >
              <v-text-field
                v-model="sector.capacity"
                :min="0"
                type="number"
                variant="outlined"
                color="primary"
                density="compact"
                label="Capacidade"
              />
            </v-col>
            <v-col
              cols="6"
              md="4"
              class="pb-0"
            >
              <v-select
                v-model="sector.seating_type"
                :items="seatingTypeItems"
                label="Tipo de Lugar"
                color="primary"
                variant="outlined"
                density="compact"
              />
            </v-col>
          </v-row>

          <v-divider class="my-6" />

          <v-btn
            class="text-capitalize text-body-1 px-0 mb-6"
            append-icon="fa fa-plus-square"
            variant="text"
            color="primary"
            density="compact"
            @click="createSection()"
          >
            <h3>Adicionar Seções</h3>
          </v-btn>

          <v-card
            v-for="(section, index) in sector.sections"
            :key="index"
            class="mb-3"
            elevation="6"
          >
            <v-card-text>
              <v-row class="mt-1">
                <v-col
                  class="pb-0"
                >
                  <v-text-field
                    v-model="section.name"
                    label="Nome"
                    variant="outlined"
                    color="primary"
                    density="compact"
                    required
                    :rules="[
                      required,
                      v => sector.sections.filter(s => s.name === section.name).length <= 1 || 'As seções não podem ter nomes repetidos'
                    ]"
                  />
                </v-col>
                <v-col
                  class="pb-0"
                >
                  <v-select
                    v-model="section.active"
                    :items="statusItems"
                    label="Ativo"
                    color="primary"
                    variant="outlined"
                    density="compact"
                  />
                </v-col>
                <v-col
                  cols="1"
                  class="pl-0"
                >
                  <v-btn
                    icon
                    :disabled="loader.loading"
                    density="compact"
                    min-height="32"
                    min-width="32"
                    elevation="1"
                    color="error"
                    @click="deleteSection(section)"
                  >
                    <v-icon
                      icon="fa-solid fa-trash"
                      size="x-small"
                    />
                  </v-btn>
                </v-col>
              </v-row>
              <v-row v-if="sector.seating_type !== SeatingType.GENERAL">
                <v-col class="pt-0">
                  <v-tooltip
                    :disabled="savedSectionIds.has(section.id)"
                    text="Salve o mapeamento antes de editar as cadeiras desta seção."
                    location="bottom"
                  >
                    <template #activator="{ props: tip }">
                      <span v-bind="tip">
                        <v-btn
                          variant="tonal"
                          color="primary"
                          class="text-none"
                          prepend-icon="fa-solid fa-chair"
                          :disabled="!savedSectionIds.has(section.id) || loader.loading"
                          @click="seatEditorSection = section"
                        >
                          Editar cadeiras
                        </v-btn>
                      </span>
                    </template>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn
          color="red darken-1"
          variant="text"
          @click="cancel"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="green darken-1"
          variant="text"
          @click="save"
        >
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <seat-editor-dialog
    v-if="seatEditorSection"
    :model-value="true"
    :section-id="seatEditorSection.id"
    :title="`${sector.name} › ${seatEditorSection.name}`"
    @update:model-value="seatEditorSection = null"
  />
</template>

<script lang="ts" setup>
import Sector from '@/types/Sector'
import { SeatingType, SeatingTypeLabels } from '@/constants/SeatingTypes'
import { v4 } from 'uuid'
import Section from '@/types/Section'
import { useSnackBar } from '@/stores/snackbar'
import { computed, ref, useTemplateRef, type Ref } from 'vue'
import { useLoader } from '@/stores/loader'
import { required } from '@/helpers/rules/required'
import { useDialog } from '@/stores/dialog'
import SectionService from '@/services/SectionService'
import type { VForm } from 'vuetify/lib/components'
import SeatEditorDialog from './seat-editor/seat-editor-dialog.vue'

interface Props {
  dialog: boolean
  editing?: boolean
  /** Sections the API already has: only those have seats to edit */
  savedSectionIds?: Set<string>
}

const snackbar = useSnackBar()
const loader = useLoader()
const dialogStore = useDialog()

const { dialog, editing = false, savedSectionIds = new Set<string>() } = defineProps<Props>()

const sector = defineModel<Sector>('sector', { required: true })

const emit = defineEmits(['save', 'cancel'])

const open = computed({
  // Little hack to hide the sections dialog when the global dialog is open
  get: () => dialog && !dialogStore.isOpen,
  set: () => cancel(),
})

const statusItems = [
  { title: 'Sim', value: true },
  { title: 'Não', value: false },
]

const seatingTypeItems = Object.values(SeatingType).map(value => ({ title: SeatingTypeLabels[value], value }))

const form = useTemplateRef<VForm>('form')

async function save() {
  if (sector.value.sections.length <= 0) {
    snackbar.show(
      'O setor deve conter pelo menos uma seção',
      'warning',
    )
    return
  }

  await form.value?.validate()

  if (form.value?.isValid) {
    emit('save')
  }
}

function cancel() {
  emit('cancel')
}

// So we know on edit mode if we should ask for confirmation when deleting them
const recentlyCreatedSections = ref<Section[]>([])

function createSection(name?: string) {
  const section = {
    id: v4(),
    sector_id: sector.value.id,
    active: true,
    name: name ?? `Nova Seção - ${sector.value.sections.length + 1}`,
    rows: [],
  }

  sector.value.sections.unshift(section)

  recentlyCreatedSections.value.push(section)
}

async function deleteSection(section: Section) {
  if (!editing || recentlyCreatedSections.value.includes(section)) {
    sector.value.sections = sector.value.sections.filter(s => s !== section)
    return
  }

  const confirmed = await dialogStore.open({
    title: 'Confirmação de deleção.',
    message: 'Realmente deseja excluir esta seção? Esta ação é irreversível',
  })

  if (!confirmed) return

  try {
    loader.increment()

    await (new SectionService()).destroy(section.id)

    sector.value.sections = sector.value.sections.filter(s => s !== section)

    snackbar.show(
      'Seção excluída com sucesso.',
      'success',
    )
  } finally {
    loader.decrement()
  }
}

/** The Section whose seats are open in the seat editor */
const seatEditorSection: Ref<Section | null> = ref(null)

</script>
